import { GamePlayScene } from '../scenes/GamePlayScene';
import { Pet } from '../entities/pets/Pet';
import { LaserPet } from '../entities/pets/LaserPet';
// import { MissilePet } from '../entities/pets/MissilePet';
// import { DronePet } from '../entities/pets/DronePet';
// import { ShieldPet } from '../entities/pets/ShieldPet';
// import { SupportPet } from '../entities/pets/SupportPet';
import { AudioManager, SoundEffect } from '../systems/AudioManager';
import { t } from '../i18n/lang';

export interface PetUpgradeResult {
  success: boolean;
  cost: number;
  newCredits: number;
  newLevel: number;
}

export class PetGamePlayManager {
  private scene: GamePlayScene;
  private audioManager: AudioManager;
  
  // Pet management
  private activePet: Pet | null = null;
  private petPanel: Phaser.GameObjects.Container | null = null;
  private petStatsPanel: Phaser.GameObjects.Container | null = null;

  constructor(scene: GamePlayScene) {
    this.scene = scene;
    this.audioManager = AudioManager.getInstance(scene);
  }

  createPetPanel(equippedPets: string[]): void {
    if (equippedPets.length === 0) return;
    
    this.petPanel = this.scene.add.container(20, this.scene.cameras.main.height - 300);
    this.petPanel.setDepth(1200);
    
    // Background
    const bg = this.scene.add.graphics();
    bg.fillGradientStyle(0x444466, 0x5a5a7a, 0x444466, 0x5a5a7a, 0.95);
    bg.fillRoundedRect(-100, -60, 200, 120, 10);
    bg.lineStyle(2, 0x9b59b6, 0.8);
    bg.strokeRoundedRect(-100, -60, 200, 120, 10);
    this.petPanel.add(bg);
    
    // Title
    const title = this.scene.add.text(-90, -50, 'Pet Control', {
      fontSize: '14px',
      color: '#9b59b6',
      fontFamily: 'Arial Black',
      fontStyle: 'bold'
    });
    this.petPanel.add(title);
    
    // Pet selection buttons
    equippedPets.forEach((petType, index) => {
      const petButton = this.scene.add.text(-90, -20 + index * 25, 
        this.getPetDisplayName(petType), {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: this.activePet?.constructor.name.includes(petType) ? '#9b59b6' : '#666666',
        padding: { x: 8, y: 4 },
        fontFamily: 'Arial'
      }).setInteractive({ useHandCursor: true });
      
      petButton.on('pointerdown', () => {
        this.spawnActivePet(petType);
      });
      
      petButton.on('pointerover', () => {
        petButton.setStyle({ backgroundColor: '#8e44ad' });
      });
      
      petButton.on('pointerout', () => {
        petButton.setStyle({ 
          backgroundColor: this.activePet?.constructor.name.includes(petType) ? '#9b59b6' : '#666666'
        });
      });
      
      this.petPanel?.add(petButton);
    });
    
    // Upgrade button
    const upgradeBtn = this.scene.add.text(-90, 30, 'Upgrade Pet', {
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: '#FF9800',
      padding: { x: 8, y: 4 },
      fontFamily: 'Arial'
    }).setInteractive({ useHandCursor: true });
    
    upgradeBtn.on('pointerdown', () => {
      if (this.activePet) {
        (this.scene as any).upgradePetFromManager(this.activePet);
      }
    });
    
    upgradeBtn.on('pointerover', () => {
      upgradeBtn.setStyle({ backgroundColor: '#F57C00' });
    });
    
    upgradeBtn.on('pointerout', () => {
      upgradeBtn.setStyle({ backgroundColor: '#FF9800' });
    });
    
    this.petPanel.add(upgradeBtn);
    
    // Add to scene's UI manager if available
    const uiManager = (this.scene as any).gamePlayManager?.getUIManager();
    if (uiManager?.uiElements) {
      uiManager.uiElements.sideElements.push(this.petPanel);
    }
  }

  spawnActivePet(petType: string): Pet | null {
    // Remove existing pet
    if (this.activePet) {
      this.activePet.destroy();
      this.activePet = null;
    }
    
    // Create new pet
    const spawnX = this.scene.cameras.main.centerX + 80;
    const spawnY = this.scene.cameras.main.centerY;
    
    switch (petType) {
      case 'laser':
        this.activePet = new LaserPet(this.scene, spawnX, spawnY);
        break;
    //   case 'missile':
    //     this.activePet = new MissilePet(this.scene, spawnX, spawnY);
    //     break;
    //   case 'drone':
    //     this.activePet = new DronePet(this.scene, spawnX, spawnY);
    //     break;
    //   case 'shield':
    //     this.activePet = new ShieldPet(this.scene, spawnX, spawnY);
    //     break;
    //   case 'support':
    //     this.activePet = new SupportPet(this.scene, spawnX, spawnY);
    //     break;
      default:
        return null;
    }
    
    if (this.activePet) {
      this.scene.add.existing(this.activePet);
      this.audioManager.playSFX(SoundEffect.PET_SUMMON);
      this.updatePetPanel();
      this.createPetStatsPanel();
    }
    
    return this.activePet;
  }

  upgradePet(pet: Pet, energyCredits: number): PetUpgradeResult {
    const currentLevel = pet.getLevel();
    const upgradeCost = this.calculatePetUpgradeCost(currentLevel);
    
    if (energyCredits < upgradeCost) {
      return {
        success: false,
        cost: upgradeCost,
        newCredits: energyCredits,
        newLevel: currentLevel
      };
    }
    
    // Upgrade pet
    pet.upgrade();
    
    // Play upgrade sound
    this.audioManager.playSFX(SoundEffect.PET_UPGRADE);
    
    // Show upgrade effect
    this.showPetUpgradeEffect(pet);
    
    const newCredits = energyCredits - upgradeCost;
    const newLevel = pet.getLevel();
    
    // Update stats panel
    this.updatePetStatsPanel();
    
    return {
      success: true,
      cost: upgradeCost,
      newCredits,
      newLevel
    };
  }

  updatePetBehavior(time: number, delta: number): void {
    if (!this.activePet) return;
    
    // Update pet AI and behavior
    this.activePet.update(time, delta);
    
    // Apply speed boosts from ad manager
    const speedBoost = (this.scene as any).adBoostManager?.getBoostMultiplier('pet_speed') || 1.0;
    if (speedBoost > 1.0) {
      const petAny = this.activePet as any;
      if (petAny.setSpeed) {
        petAny.setSpeed(petAny.baseSpeed * speedBoost);
      }
    }
    
    // Check if pet needs to target monsters
    this.updatePetTargeting();
  }

  private updatePetTargeting(): void {
    if (!this.activePet) return;
    
    // Find nearby monsters for pet to target
    const monsters = this.scene.children.list.filter(child => 
      child.constructor.name.includes('Alien') || child.constructor.name.includes('Boss')
    );
    
    if (monsters.length > 0) {
      const petAny = this.activePet as any;
      if (petAny.setTarget) {
        // Find closest monster
        let closestMonster = monsters[0];
        let minDistance = Phaser.Math.Distance.Between(
          this.activePet.x, this.activePet.y,
          (closestMonster as any).x, (closestMonster as any).y
        );
        
        for (const monster of monsters) {
          const distance = Phaser.Math.Distance.Between(
            this.activePet.x, this.activePet.y,
            (monster as any).x, (monster as any).y
          );
          if (distance < minDistance) {
            minDistance = distance;
            closestMonster = monster;
          }
        }
        
        petAny.setTarget(closestMonster);
      }
    }
  }

  private createPetStatsPanel(): void {
    if (!this.activePet) return;
    
    this.petStatsPanel = this.scene.add.container(250, this.scene.cameras.main.height - 200);
    
    // Background
    const bg = this.scene.add.rectangle(0, 0, 180, 100, 0x333355, 0.9);
    this.petStatsPanel.add(bg);
    
    // Pet info
    const petName = this.getPetDisplayName(this.activePet.constructor.name);
    const title = this.scene.add.text(-80, -40, `${petName} Lv.${this.activePet.getLevel()}`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    });
    this.petStatsPanel.add(title);
    
    // Stats
    const stats = this.getPetStatsText(this.activePet);
    const statsText = this.scene.add.text(-80, -15, stats, {
      fontSize: '11px',
      color: '#cccccc',
      fontFamily: 'Arial'
    });
    this.petStatsPanel.add(statsText);
    
    // Health bar
    this.createPetHealthBar();
  }

  private createPetHealthBar(): void {
    if (!this.activePet || !this.petStatsPanel) return;
    
    const healthRatio = this.activePet.getHealth() / this.activePet.getMaxHealth();
    
    // Health bar background
    const healthBg = this.scene.add.rectangle(-80, 20, 120, 8, 0x666666);
    this.petStatsPanel.add(healthBg);
    
    // Health bar fill
    const healthFill = this.scene.add.rectangle(
      -80, 20, 120 * healthRatio, 8, 0x4CAF50
    );
    this.petStatsPanel.add(healthFill);
    
    // Health text
    const healthText = this.scene.add.text(-20, 35, 
      `${Math.round(this.activePet.getHealth())}/${this.activePet.getMaxHealth()}`, {
      fontSize: '10px',
      color: '#ffffff',
      fontFamily: 'Arial'
    });
    this.petStatsPanel.add(healthText);
  }

  private updatePetPanel(): void {
    // Update pet selection button colors
    if (!this.petPanel) return;
    
    this.petPanel.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text && 
          child.text.includes('Pet') === false && 
          child.text.includes('Control') === false &&
          child.text.includes('Upgrade') === false) {
        const isActive = this.activePet?.constructor.name.includes(child.text.split(' ')[0] ?? '');
        child.setStyle({ 
          backgroundColor: isActive ? '#4CAF50' : '#666666' 
        });
      }
    });
  }

  private updatePetStatsPanel(): void {
    if (!this.petStatsPanel || !this.activePet) return;
    
    // Destroy old panel and create new one
    this.petStatsPanel.destroy();
    this.createPetStatsPanel();
  }

  private showPetUpgradeEffect(pet: Pet): void {
    // Create upgrade particles around pet
    for (let i = 0; i < 8; i++) {
      const particle = this.scene.add.circle(
        pet.x + (Math.random() - 0.5) * 40,
        pet.y + (Math.random() - 0.5) * 40,
        3,
        0x00ff88
      );
      
      this.scene.tweens.add({
        targets: particle,
        y: particle.y - 60,
        alpha: 0,
        scaleX: 0.1,
        scaleY: 0.1,
        duration: 1200,
        ease: 'Power2',
        delay: i * 80,
        onComplete: () => particle.destroy()
      });
    }
    
    // Show level up text
    const levelText = this.scene.add.text(
      pet.x,
      pet.y - 40,
      `Lv.${pet.getLevel()}!`,
      {
        fontSize: '16px',
        color: '#00ff88',
        fontFamily: 'Arial',
        fontStyle: 'bold'
      }
    ).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: levelText,
      y: levelText.y - 30,
      alpha: 0,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => levelText.destroy()
    });
  }

  private calculatePetUpgradeCost(level: number): number {
    return 200 + (level - 1) * 100;
  }

  private getPetDisplayName(petType: string): string {
    switch (petType.toLowerCase()) {
      case 'laser':
      case 'laserpet':
        return 'Laser Pet';
      case 'missile':
      case 'missilepet':
        return 'Missile Pet';
      case 'drone':
      case 'dronepet':
        return 'Drone Pet';
      case 'shield':
      case 'shieldpet':
        return 'Shield Pet';
      case 'support':
      case 'supportpet':
        return 'Support Pet';
      default:
        return petType;
    }
  }

  private getPetStatsText(pet: Pet): string {
    const petAny = pet as any;
    let stats = `HP: ${pet.getHealth()}/${pet.getMaxHealth()}\n`;
    
    if (petAny.getDamage) {
      stats += `Damage: ${petAny.getDamage()}\n`;
    }
    if (petAny.getSpeed) {
      stats += `Speed: ${petAny.getSpeed()}\n`;
    }
    if (petAny.getRange) {
      stats += `Range: ${petAny.getRange()}`;
    }
    
    return stats;
  }

  // Pet AI helpers
  makePetFollowEarth(earth: any): void {
    if (!this.activePet || !earth) return;
    
    // Simple follow logic
    const distance = Phaser.Math.Distance.Between(
      this.activePet.x, this.activePet.y,
      earth.x, earth.y
    );
    
    if (distance > 120) {
      // Move towards Earth
      const angle = Phaser.Math.Angle.Between(
        this.activePet.x, this.activePet.y,
        earth.x, earth.y
      );
      
      const petAny = this.activePet as any;
      const speed = petAny.getSpeed ? petAny.getSpeed() : 50;
      
      this.activePet.x += Math.cos(angle) * speed * 0.016; // 60fps
      this.activePet.y += Math.sin(angle) * speed * 0.016;
    }
  }

  makePetOrbitEarth(earth: any, time: number): void {
    if (!this.activePet || !earth) return;
    
    // Orbital movement around Earth
    const radius = 80;
    const speed = 0.001; // radians per ms
    const angle = time * speed;
    
    this.activePet.x = earth.x + Math.cos(angle) * radius;
    this.activePet.y = earth.y + Math.sin(angle) * radius;
  }

  // Getters
  getActivePet(): Pet | null {
    return this.activePet;
  }

  hasPet(): boolean {
    return this.activePet !== null;
  }

  getPetLevel(): number {
    return this.activePet ? this.activePet.getLevel() : 0;
  }

  getPetHealth(): number {
    return this.activePet ? this.activePet.getHealth() : 0;
  }

  getPetMaxHealth(): number {
    return this.activePet ? this.activePet.getMaxHealth() : 0;
  }

  // Cleanup
  destroyActivePet(): void {
    if (this.activePet) {
      this.activePet.destroy();
      this.activePet = null;
    }
    
    if (this.petStatsPanel) {
      this.petStatsPanel.destroy();
      this.petStatsPanel = null;
    }
  }

  cleanup(): void {
    this.destroyActivePet();
    
    if (this.petPanel) {
      this.petPanel.destroy();
      this.petPanel = null;
    }
  }
} 